/**
 * Google Picker API Loader Utility
 * Handles loading Google API scripts and initializing pickers
 */

let googleAPIsLoaded = false;
let googlePickerAPILoaded = false;
let googlePhotosPickerAPILoaded = false;

/**
 * Load Google APIs (gapi and picker)
 */
export async function loadGoogleAPIs() {
  if (googleAPIsLoaded) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    // Load gapi script
    if (!window.gapi) {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        // Load picker API
        window.gapi.load('picker', {
          callback: () => {
            googleAPIsLoaded = true;
            googlePickerAPILoaded = true;
            resolve();
          },
          onerror: reject
        });
      };
      script.onerror = reject;
      document.head.appendChild(script);
    } else {
      // gapi already loaded, just load picker
      if (googlePickerAPILoaded) {
        resolve();
      } else {
        window.gapi.load('picker', {
          callback: () => {
            googlePickerAPILoaded = true;
            resolve();
          },
          onerror: reject
        });
      }
    }
  });
}

/**
 * Load Google Photos Picker API
 * Note: Google Photos Picker uses a different API structure
 * This may need to be updated based on actual API documentation
 */
export async function loadGooglePhotosPickerAPI() {
  if (googlePhotosPickerAPILoaded) {
    return Promise.resolve();
  }

  // First ensure base Google APIs are loaded
  await loadGoogleAPIs();

  // Google Photos Picker API loading
  // Note: This may need adjustment based on actual API implementation
  // The API might be part of the same picker API or separate
  return new Promise((resolve, reject) => {
    // For now, assume it's part of the same picker API
    // If separate, we'll need to load additional scripts
    if (googlePickerAPILoaded) {
      googlePhotosPickerAPILoaded = true;
      resolve();
    } else {
      loadGoogleAPIs().then(() => {
        googlePhotosPickerAPILoaded = true;
        resolve();
      }).catch(reject);
    }
  });
}

/**
 * Get access token for a specific OAuth account
 * @param {string} provider - Provider name (e.g., 'google-drive', 'google-photos')
 * @param {string} accountId - Account identifier
 * @returns {Promise<string>} Access token
 */
export async function getAccessToken(provider, accountId) {
  try {
    const response = await fetch(
      `/api/media-sources/picker/token?provider=${encodeURIComponent(provider)}&accountId=${encodeURIComponent(accountId)}`,
      {
        credentials: 'include'
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get access token: ${response.status}`);
    }

    const data = await response.json();
    return data.access_token;
  } catch (error) {
    window.logger?.error('Error getting access token:', error);
    throw error;
  }
}

/**
 * Get Google Picker API key from authenticated API endpoint
 * This is more secure than embedding it in HTML
 */
let cachedApiKey = null;

export async function getGooglePickerAPIKey() {
  // Return cached key if available
  if (cachedApiKey) {
    return cachedApiKey;
  }

  try {
    const response = await fetch('/api/media-sources/picker/api-key', {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to get API key: ${response.status}`);
    }

    const data = await response.json();
    cachedApiKey = data.api_key;
    return cachedApiKey;
  } catch (error) {
    window.logger?.error('Error getting Google Picker API key:', error);
    throw new Error('Google Picker API key not available. Please ensure you are logged in.', { cause: error });
  }
}

/**
 * Get Google Cloud project number (app ID) from authenticated API endpoint
 * Required for drive.file scope
 */
let cachedAppId = null;

export async function getGooglePickerAppId() {
  // Return cached app ID if available
  if (cachedAppId) {
    return cachedAppId;
  }

  try {
    const response = await fetch('/api/media-sources/picker/app-id', {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to get app ID: ${response.status}`);
    }

    const data = await response.json();
    cachedAppId = data.app_id;
    return cachedAppId;
  } catch (error) {
    window.logger?.error('Error getting Google Picker app ID:', error);
    throw new Error('Google Cloud project number not available. Please ensure you are logged in.', { cause: error });
  }
}

